import z from "zod";
import { IsFieldValid } from "../shared/interfaces";

/** IST timezone for all backend (UTC) date display */
const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Date and time in IST (includes hours, minutes, seconds, AM/PM).
 * For table/list date columns without time, use {@link formatDateOnly} instead.
 */
export function formatDate(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return 'N/A';
    try {
        const date = parseAsUTC(dateInput);
        if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateInput);
            return 'Invalid Date';
        }
        const s = date.toLocaleString('en-IN', {
            timeZone: IST_TIMEZONE,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        });
        return s.replace(/,/g, '');
    } catch (error) {
        console.error('Error formatting date:', error, 'Input:', dateInput);
        return 'Error';
    }
}

/**
 * Date-only in IST as DD/MM/YYYY (no time, no AM/PM). Use for tables and lists.
 */
export function formatDateOnly(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '';
    try {
        const date = parseAsUTC(dateInput);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB', {
            timeZone: IST_TIMEZONE,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    } catch {
        return '';
    }
}

/**
 * Backend dates are UTC. Parse string/Date so we can format in IST.
 * Date-only strings (YYYY-MM-DD) are treated as midnight UTC.
 * ISO strings without timezone (e.g. "2024-01-15T10:30:00") are treated as UTC.
 */
function parseAsUTC(dateInput: string | Date): Date {
    if (dateInput instanceof Date) return dateInput;
    const s = String(dateInput).trim();
    if (!s) return new Date(NaN);
    if (s.includes('T')) {
        // ISO format: if no Z or timezone offset, backend sends UTC - append Z to parse as UTC
        const hasTimezone = /[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
        return new Date(hasTimezone ? s : s + 'Z');
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00.000Z');
    if (s.includes('-')) return new Date(s.replace(/-/g, '/'));
    return new Date(s);
}

/**
 * Format a UTC date for display in IST (date + time). Alias for formatDate for clarity at call sites.
 */
export function formatDateTimeIST(dateInput: string | Date | null | undefined): string {
    return formatDate(dateInput);
}

/**
 * Returns a Date's date-part as YYYY-MM-DD in IST.
 * en-CA locale conveniently outputs YYYY-MM-DD.
 */
export function toISTDateString(date: Date = new Date()): string {
    return date.toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE });
}

/**
 * Shortcut: today's date as YYYY-MM-DD in IST.
 */
export function getTodayDateIST(): string {
    return toISTDateString(new Date());
}

/**
 * Encode YYYY-MM-DD from &lt;input type="date"&gt; as ISO 8601 UTC for Havala recordDate (POST/PUT).
 */
export function dateInputToIsoUtc(dateStr: string): string {
    const s = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return new Date(`${s}T00:00:00.000Z`).toISOString();
    }
    const d = parseAsUTC(s);
    if (isNaN(d.getTime())) {
        return new Date().toISOString();
    }
    return d.toISOString();
}

/**
 * Move focus to the next enabled, non-readonly field in the same form (document order).
 * Skips hidden inputs and fields that cannot receive focus.
 */
export function focusNextFormField(
  current: HTMLElement,
  form: HTMLFormElement | null | undefined,
): boolean {
  if (!form || !current) return false;
  const focusables = Array.from(
    form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input:not([type="hidden"]), select, textarea',
    ),
  ).filter((el) => {
    if (el.disabled) return false;
    if ('readOnly' in el && (el as HTMLInputElement).readOnly) return false;
    return true;
  });
  const idx = focusables.indexOf(current as HTMLInputElement);
  if (idx < 0 || idx >= focusables.length - 1) return false;
  const next = focusables[idx + 1];
  next.focus();
  if (next instanceof HTMLInputElement) {
    const t = next.type;
    if (t === 'text' || t === 'search' || t === 'password' || t === 'tel' || t === 'url' || t === 'number' || t === '') {
      try {
        next.select();
      } catch {
        /* ignore */
      }
    }
  }
  return true;
}

export const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key !== 'Enter') return;
  e.preventDefault();
  if (!e.currentTarget?.form) return;
  focusNextFormField(e.currentTarget, e.currentTarget.form);
};


export const validateFieldWithSchema = <T extends Record<string, any>>(
  name: string,
  value: string,
  formData: T,
  formSchema: z.ZodObject<any>
): IsFieldValid => {
  const isFieldValid: IsFieldValid = { isValid: false, message: "" };

  try {
    const fieldSchema = formSchema.shape[name as keyof typeof formSchema.shape];

    if (fieldSchema) {
      fieldSchema.parse(value);
      isFieldValid.isValid = true;
    }
  } catch (err: any) {
    isFieldValid.isValid = false;
    isFieldValid.message = err?.errors?.[0]?.message || "";
  }
  return isFieldValid;
}

export const getGameTitle = (gameType: string): string => {
  switch (gameType) {
    case 'oneday':
      return 'One Day';
    case 'test':
      return 'Test';
    case 'session':
      return 'Session';
    default:
      return '';
  }
} 

export const cleanObject = <T extends object>(obj: T): Partial<T> => {
  return Object.entries(obj)
    .filter(([_, v]) => v !== null && v !== undefined && v !== '')
    .reduce((acc, [k, v]) => {
      (acc as any)[k] = v;
      return acc;
    }, {} as Partial<T>);
}

/**
 * Profit-loss report API may return `grandTotalProfitOrLoss` (authoritative TOTAL P&L).
 * Falls back to `fallback` (e.g. sum of match rows) when missing or invalid.
 */
export function pickGrandTotalProfitOrLoss(payload: unknown, fallback: number): number {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return fallback;
  const o = payload as Record<string, unknown>;
  const raw = o.grandTotalProfitOrLoss ?? o.GrandTotalProfitOrLoss;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}